import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../App";
import { Avatar, AvatarImage, AvatarFallback } from "../components/ui/avatar";
import { Button } from "../components/ui/button";
import {
  Target, Heart, ChevronRight, Users,
  Sparkles, ArrowRight, Eye, MessageCircle,
  HelpCircle, Compass, Star, Zap, Shield,
  TrendingUp, BookOpen, Brain, Puzzle, BarChart3, CircleDot, Plus, Link2, HeartHandshake
} from "lucide-react";
import { toast } from "sonner";
import { trackReflectionAdded, trackToolOpened } from "../utils/analytics";

const API = "/api";

// ── SVG Progress Ring ─────────────────────────────────────
const ProgressRing = ({ percent, size = 52, stroke = 3, color }) => {
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (percent / 100) * circumference;
  return (
    <div className="relative flex-shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none"
          stroke="var(--color-border-default)" strokeWidth={stroke} />
        {percent > 0 && (
          <circle cx={size / 2} cy={size / 2} r={radius} fill="none"
            stroke={color} strokeWidth={stroke}
            strokeDasharray={circumference} strokeDashoffset={offset}
            strokeLinecap="round" className="transition-all duration-700" />
        )}
      </svg>
      <span className="absolute inset-0 flex items-center justify-center"
        style={{ fontSize: 11, fontWeight: 600, color: "var(--color-text-primary)" }}>
        {percent}%
      </span>
    </div>
  );
};

// ── Discovery Badge ───────────────────────────────────────
const DiscoveryBadge = ({ icon: Icon, label, color, bg, border }) => (
  <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold transition-all"
    style={{ background: bg, color, border: `1px solid ${border}` }}>
    <Icon className="w-3 h-3" />
    <span className="truncate max-w-[160px]">{label}</span>
  </div>
);

// ── Empty Discovery Slot ──────────────────────────────────
const EmptySlot = ({ label, onClick }) => (
  <button onClick={onClick}
    className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-all hover:opacity-80 cursor-pointer"
    style={{
      background: "transparent",
      color: "var(--color-text-placeholder)",
      border: "1px dashed var(--color-border-active)",
    }}>
    <HelpCircle className="w-3 h-3" />
    <span>{label}</span>
  </button>
);

// ── Mini Wheel (for Living Profile) ───────────────────────
const MiniWheel = ({ areas, userPicture, onClick, size: propSize }) => {
  const size = propSize || 80;
  const cx = size / 2;
  const cy = size / 2;
  const outerR = size / 2 - 4;
  const innerR = size * 0.40;
  const n = areas.length;
  if (n === 0) return null;
  const angleStep = (2 * Math.PI) / n;
  const polar = (angle, r) => ({ x: cx + r * Math.cos(angle - Math.PI / 2), y: cy + r * Math.sin(angle - Math.PI / 2) });

  return (
    <button onClick={onClick} className="flex-shrink-0 transition-transform hover:scale-105" data-testid="mini-wheel">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {areas.map((area, i) => {
          if (!area.score) return null;
          const startAngle = i * angleStep;
          const endAngle = (i + 1) * angleStep;
          const r = innerR + ((outerR - innerR) * area.score) / 10;
          const s1 = polar(startAngle, innerR); const e1 = polar(startAngle, r);
          const s2 = polar(endAngle, innerR); const e2 = polar(endAngle, r);
          const la = endAngle - startAngle > Math.PI ? 1 : 0;
          const d = `M ${s1.x} ${s1.y} L ${e1.x} ${e1.y} A ${r} ${r} 0 ${la} 1 ${e2.x} ${e2.y} L ${s2.x} ${s2.y} A ${innerR} ${innerR} 0 ${la} 0 ${s1.x} ${s1.y} Z`;
          return <path key={i} d={d} fill={area.color} opacity={0.6} stroke={area.color} strokeWidth={0.5} />;
        })}
        <defs><clipPath id="mini-clip"><circle cx={cx} cy={cy} r={innerR} /></clipPath></defs>
        {userPicture ? (
          <image href={userPicture} x={cx - innerR} y={cy - innerR} width={innerR * 2} height={innerR * 2}
            clipPath="url(#mini-clip)" preserveAspectRatio="xMidYMid slice" />
        ) : (
          <circle cx={cx} cy={cy} r={innerR} fill="var(--color-surface)" />
        )}
        <circle cx={cx} cy={cy} r={innerR} fill="none" stroke="white" strokeWidth={1.5} />
      </svg>
    </button>
  );
};

// ── Inline Add Input ──────────────────────────────────────
const InlineAddInput = ({ color, placeholder, onAdd, testIdPrefix }) => {
  const [value, setValue] = useState("");
  const handleAdd = () => {
    const text = value.trim();
    if (!text) return;
    onAdd(text);
    setValue("");
  };
  return (
    <div className="flex gap-1 mt-1">
      <input
        type="text"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && handleAdd()}
        placeholder={placeholder}
        className="flex-1 text-[11px] rounded-md px-2 py-1 focus:outline-none"
        style={{
          background: "var(--color-surface-subtle)",
          color: "var(--color-text-primary)",
          border: "1px solid var(--color-border-default)",
        }}
        data-testid={`${testIdPrefix}-input`}
      />
      <button onClick={handleAdd} disabled={!value.trim()}
        className="text-[10px] font-semibold px-2 py-1 rounded-md transition-all disabled:opacity-30"
        style={{ background: `${color}15`, color: color, border: `1px solid ${color}30` }}
        data-testid={`${testIdPrefix}-add-btn`}>
        <Plus className="w-3 h-3" />
      </button>
    </div>
  );
};

// ── Focus Areas Spotlight ─────────────────────────────────
const FocusSpotlight = ({ wheel, navigate, onWheelUpdate, swotData }) => {
  const areas = wheel?.areas || [];
  const focusKeys = wheel?.focus_areas || [];
  if (focusKeys.length === 0) return null;

  const areaMap = {};
  for (const a of areas) areaMap[a.key] = a;
  const focusAreas = focusKeys.map(k => areaMap[k]).filter(Boolean);
  if (focusAreas.length === 0) return null;

  // Build linked SWOT items map per area
  const linkedMap = {};
  if (swotData) {
    for (const cat of ["strengths", "weaknesses", "opportunities"]) {
      for (const item of (swotData[cat] || [])) {
        if (item.linked_wheel_area && !item.deleted) {
          const areaKey = item.linked_wheel_area;
          if (!linkedMap[areaKey]) linkedMap[areaKey] = [];
          linkedMap[areaKey].push({
            content: item.content || item.text || "",
            field: item.linked_reflection_field || "",
            suggested_by_name: item.suggested_by_name,
            suggested_by_picture: item.suggested_by_picture,
          });
        }
      }
    }
  }

  const handleAddItem = async (areaKey, field, text) => {
    try {
      const res = await fetch(`${API}/tools/wheel/reflection`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ area_key: areaKey, field, text }),
      });
      if (!res.ok) throw new Error("Failed to save");
      const updated = await res.json();
      if (onWheelUpdate) onWheelUpdate(updated);
      trackReflectionAdded({ areaKey, field, source: "myself" });
      toast.success("Added", { duration: 1200 });
    } catch {
      toast.error("Could not save");
    }
  };

  const renderSection = (area, label, field, items, placeholder) => {
    const linked = (linkedMap[area.key] || []).filter(i => i.field === field);
    return (
      <div className="mb-1.5">
        <p className="text-[10px] font-semibold mb-0.5" style={{ color: area.color }}>{label}</p>
        {items.map((item, i) => (
          <div key={i} className="flex items-start gap-1.5">
            <div className="w-1 h-1 rounded-full mt-[5px] flex-shrink-0" style={{ background: area.color }} />
            <p className="text-xs leading-relaxed" style={{ color: "var(--color-text-secondary)" }}>{item}</p>
          </div>
        ))}
        {linked.map((item, i) => (
          <div key={`linked-${i}`} className="flex items-start gap-1.5" data-testid={`focus-linked-${area.key}-${field}-${i}`}>
            <Link2 className="w-3 h-3 mt-[3px] text-violet-400 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-xs leading-relaxed text-slate-600">{item.content}</p>
              {item.suggested_by_name && (
                <div className="flex items-center gap-1 mt-0.5">
                  <Avatar className="h-3.5 w-3.5">
                    {item.suggested_by_picture && <AvatarImage src={item.suggested_by_picture} alt={item.suggested_by_name} />}
                    <AvatarFallback className="bg-violet-100 text-violet-600 text-[6px]">
                      {item.suggested_by_name?.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                  <span className="text-[9px] text-violet-500">by {item.suggested_by_name}</span>
                </div>
              )}
            </div>
          </div>
        ))}
        <InlineAddInput
          color={area.color}
          placeholder={placeholder}
          onAdd={(text) => handleAddItem(area.key, field, text)}
          testIdPrefix={`focus-${field}-${area.key}`}
        />
      </div>
    );
  };

  return (
    <div className="mb-8" data-testid="focus-spotlight">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-base font-semibold" style={{ color: "var(--color-text-primary)" }}>
          Your Focus
        </h3>
        <button onClick={() => navigate("/tools/wheel")}
          className="text-xs font-medium flex items-center gap-1 transition-colors"
          style={{ color: "var(--color-text-interactive)" }}
          data-testid="focus-spotlight-link">
          Edit in Wheel <ChevronRight className="w-3 h-3" />
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {focusAreas.map(area => {
          const whyList = Array.isArray(area.why_score) ? area.why_score : (area.why_score ? [area.why_score] : []);
          const improveList = Array.isArray(area.what_to_improve) ? area.what_to_improve : (area.what_to_improve ? [area.what_to_improve] : []);
          const actionList = Array.isArray(area.action_steps) ? area.action_steps : (area.action_steps ? [area.action_steps] : []);

          return (
            <div key={area.key} className="rounded-xl p-4"
              style={{ background: "var(--color-surface)", border: `1.5px solid ${area.color}30` }}
              data-testid={`focus-card-${area.key}`}>
              <div className="flex items-center gap-2 mb-2.5">
                <div className="w-2.5 h-2.5 rounded-full" style={{ background: area.color }} />
                <span className="text-sm font-semibold" style={{ color: "var(--color-text-primary)" }}>{area.label}</span>
                <span className="ml-auto text-lg font-bold" style={{ color: area.color }}>{area.score || "–"}<span className="text-xs font-normal opacity-60">/10</span></span>
              </div>
              {renderSection(area, "Going well", "why_score", whyList, "Add what's going well...")}
              {renderSection(area, "To reach 10", "what_to_improve", improveList, "Add something to improve...")}
              {renderSection(area, "Next steps", "action_steps", actionList, "Add an action step...")}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ── Living Profile Card ───────────────────────────────────
const LivingProfile = ({ user, swot, values, insights, swotProgress, valuesProgress, wheel, navigate }) => {
  const strengths = (swot?.strengths || []).filter(s => !s.deleted);
  const topStrengths = strengths.slice(0, 2);
  const topValues = (values?.values || []).sort((a, b) => a.priority - b.priority).slice(0, 3);
  const totalInsights = insights.swot.length + insights.values.length;
  const hasSwotData = strengths.length > 0;
  const hasValuesData = topValues.length > 0;

  // Dynamic subtitle
  let subtitle;
  if (!hasSwotData && !hasValuesData) {
    subtitle = "Start exploring who you are. Every tool adds a new dimension.";
  } else if (hasSwotData && !hasValuesData) {
    subtitle = "Your strengths are emerging. Discover your values next.";
  } else if (!hasSwotData && hasValuesData) {
    subtitle = "Your values are clear. Now map your strengths and opportunities.";
  } else if (swotProgress === 100 && valuesProgress === 100) {
    subtitle = "Your self-portrait is taking shape. Keep refining as you grow.";
  } else {
    subtitle = "Your self-portrait is coming together. Keep going.";
  }

  return (
    <div className="rounded-2xl overflow-hidden" data-testid="living-profile"
      style={{ background: "var(--color-surface)", border: "0.5px solid var(--color-border-default)" }}>

      {/* Hero gradient strip */}
      <div className="h-20 relative"
        style={{ background: "linear-gradient(135deg, #26215C 0%, #534AB7 50%, #7F77DD 100%)" }}>
        <div className="absolute -bottom-10 left-1/2 -translate-x-1/2">
          <div className="relative">
            {(wheel?.areas || []).length > 0 ? (
              <MiniWheel areas={wheel.areas} userPicture={user?.picture} onClick={() => navigate("/tools/wheel")} size={88} />
            ) : (
              <>
                <Avatar className="h-20 w-20 ring-4 ring-white shadow-lg" data-testid="profile-avatar">
                  <AvatarImage src={user?.picture} alt={user?.name} />
                  <AvatarFallback className="text-2xl font-bold"
                    style={{ background: "linear-gradient(145deg, #534AB7, #7F77DD)", color: "#EEEDFE" }}>
                    {user?.name?.charAt(0) || "?"}
                  </AvatarFallback>
                </Avatar>
                {/* Completion ring overlay */}
                <div className="absolute -inset-1 pointer-events-none">
                  <svg width="88" height="88" className="-rotate-90">
                    <circle cx="44" cy="44" r="41" fill="none"
                      stroke="rgba(255,255,255,0.2)" strokeWidth="2.5" />
                    <circle cx="44" cy="44" r="41" fill="none"
                      stroke="#AFA9EC" strokeWidth="2.5"
                      strokeDasharray={2 * Math.PI * 41}
                      strokeDashoffset={2 * Math.PI * 41 * (1 - (swotProgress + valuesProgress) / 200)}
                      strokeLinecap="round" className="transition-all duration-1000" />
                  </svg>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Profile content */}
      <div className="pt-14 pb-6 px-6 text-center">
        <h2 className="text-xl font-bold" style={{ color: "var(--color-text-primary)" }} data-testid="profile-name">
          {user?.name}
        </h2>
        <p className="text-sm mt-1 max-w-sm mx-auto" style={{ color: "var(--color-text-secondary)" }}>
          {subtitle}
        </p>

        {/* Discovery badges — the living layer */}
        <div className="mt-5 space-y-3">
          {/* Row 1: Strengths */}
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider mb-2"
              style={{ color: "var(--color-strengths-text)" }}>
              Top Strengths
            </p>
            <div className="flex items-center justify-center gap-2 flex-wrap">
              {topStrengths.map((s, i) => (
                <DiscoveryBadge key={s.id || i}
                  icon={Zap} label={s.text || s.content || "Strength"}
                  color="var(--color-strengths-text)"
                  bg="var(--color-strengths-bg)"
                  border="var(--color-strengths-border)" />
              ))}
              {topStrengths.length === 0 && (
                <EmptySlot label="Discover strengths" onClick={() => navigate("/tools/swot")} />
              )}
              {topStrengths.length === 1 && (
                <EmptySlot label="Add more" onClick={() => navigate("/tools/swot")} />
              )}
            </div>
          </div>

          {/* Row 2: Core Values */}
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider mb-2"
              style={{ color: "var(--color-brand-primary)" }}>
              Core Values
            </p>
            <div className="flex items-center justify-center gap-2 flex-wrap">
              {topValues.map((v) => (
                <DiscoveryBadge key={v.value_id}
                  icon={Heart} label={v.name}
                  color="var(--color-brand-primary)"
                  bg="var(--color-brand-ghost)"
                  border="var(--color-border-active)" />
              ))}
              {topValues.length === 0 && (
                <EmptySlot label="Discover values" onClick={() => navigate("/tools/values")} />
              )}
            </div>
          </div>

          {/* Row 3: Circle Insights */}
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider mb-2"
              style={{ color: "var(--color-text-interactive)" }}>
              Circle Perspective
            </p>
            <div className="flex items-center justify-center gap-2 flex-wrap">
              {totalInsights > 0 ? (
                <DiscoveryBadge
                  icon={Eye} label={`${totalInsights} insight${totalInsights > 1 ? "s" : ""} from your circle`}
                  color="var(--color-text-interactive)"
                  bg="var(--color-surface-subtle)"
                  border="var(--color-border-default)" />
              ) : (
                <EmptySlot label="Get outside perspective" onClick={() => navigate("/network")} />
              )}
            </div>
          </div>

          {/* Row 4: Future tools — subtle placeholders */}
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider mb-2"
              style={{ color: "var(--color-text-placeholder)" }}>
              Coming Soon
            </p>
            <div className="flex items-center justify-center gap-2 flex-wrap">
              <span className="text-[10px] px-2.5 py-1 rounded-full"
                style={{ color: "var(--color-text-placeholder)", border: "1px dashed var(--color-border-default)" }}>
                Purpose
              </span>
              <span className="text-[10px] px-2.5 py-1 rounded-full"
                style={{ color: "var(--color-text-placeholder)", border: "1px dashed var(--color-border-default)" }}>
                Personality
              </span>
              <span className="text-[10px] px-2.5 py-1 rounded-full"
                style={{ color: "var(--color-text-placeholder)", border: "1px dashed var(--color-border-default)" }}>
                Competencies
              </span>
            </div>
          </div>
        </div>

        {/* View Self-Portrait CTA */}
        <div className="mt-4 px-6 pb-6">
          <Link to="/myself/portrait"
            className="flex items-center justify-center gap-2 w-full py-2.5 rounded-full text-xs font-semibold transition-all hover:opacity-80"
            style={{ background: "var(--color-surface-subtle)", color: "var(--color-text-interactive)", border: "0.5px solid var(--color-border-default)" }}
            data-testid="view-self-portrait-link">
            <Eye className="w-3.5 h-3.5" />
            View Full Self-Portrait
            <ChevronRight className="w-3 h-3" />
          </Link>
        </div>
      </div>
    </div>
  );
};

// ── Tool Card ─────────────────────────────────────────────
const ToolCard = ({ title, description, icon: Icon, iconBg, iconColor, progress, progressColor, nudge, stats, footer, onClick, testId }) => (
  <div className="rounded-xl cursor-pointer group transition-all hover:shadow-sm"
    style={{ background: "var(--color-surface)", border: "0.5px solid var(--color-border-default)" }}
    onClick={onClick} data-testid={testId}>
    <div className="p-5">
      <div className="flex items-start gap-3.5 mb-4">
        <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ background: iconBg }}>
          <Icon className="w-5 h-5" style={{ color: iconColor }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold" style={{ color: "var(--color-text-primary)" }}>{title}</h4>
            <ProgressRing percent={progress} size={40} stroke={3} color={progressColor} />
          </div>
          <p className="text-xs mt-0.5" style={{ color: "var(--color-text-secondary)" }}>{description}</p>
        </div>
      </div>

      {/* Stats row */}
      {stats && <div className="mb-3">{stats}</div>}

      {/* Contextual nudge */}
      {nudge && (
        <div className="rounded-lg p-3 mb-1" style={{ background: "var(--color-surface-subtle)", border: "0.5px solid var(--color-border-default)" }}>
          <p className="text-xs leading-relaxed" style={{ color: "var(--color-text-secondary)" }}>
            <Sparkles className="w-3 h-3 inline mr-1" style={{ color: "var(--color-brand-mid)" }} />
            {nudge}
          </p>
        </div>
      )}
    </div>

    {/* Footer */}
    <div className="px-5 py-3 flex items-center justify-between rounded-b-xl"
      style={{ background: "var(--color-surface-subtle)", borderTop: "0.5px solid var(--color-border-default)" }}>
      <span className="text-xs" style={{ color: "var(--color-text-interactive)" }}>{footer}</span>
      <ChevronRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" style={{ color: "var(--color-text-placeholder)" }} />
    </div>
  </div>
);

// ── Upcoming Tool Teaser ──────────────────────────────────
const UpcomingTeaser = ({ icon: Icon, name, question, color }) => (
  <div className="rounded-xl p-4 transition-all hover:shadow-sm cursor-default"
    style={{ background: "var(--color-surface)", border: "0.5px solid var(--color-border-default)" }}
    data-testid={`upcoming-${name.toLowerCase().replace(/\s+/g, "-")}`}>
    <div className="flex items-start gap-3">
      <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
        style={{ background: "var(--color-surface-subtle)", border: "0.5px solid var(--color-border-default)" }}>
        <Icon className="w-4 h-4" style={{ color }} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <h5 className="text-sm font-semibold" style={{ color: "var(--color-text-primary)" }}>{name}</h5>
          <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full"
            style={{ color: "var(--color-text-interactive)", background: "var(--color-brand-ghost)", border: "0.5px solid var(--color-border-active)" }}>
            Explore soon
          </span>
        </div>
        <p className="text-xs italic" style={{ color: "var(--color-text-secondary)" }}>
          "{question}"
        </p>
      </div>
    </div>
  </div>
);

// ── Main Page ─────────────────────────────────────────────
const MyselfPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [progress, setProgress] = useState(null);
  const [swot, setSwot] = useState(null);
  const [values, setValues] = useState(null);
  const [insights, setInsights] = useState({ swot: [], values: [] });
  const [loading, setLoading] = useState(true);
  const [wheel, setWheel] = useState(null);
  const [gratitudeCount, setGratitudeCount] = useState(0);
  const [gratitudePendingAsks, setGratitudePendingAsks] = useState(0);

  const fetchData = useCallback(async () => {
    try {
      const [progressRes, swotRes, valuesRes, swotInsightsRes, valuesInsightsRes, wheelRes, gratitudeRes] = await Promise.all([
        fetch(`${API}/dashboard/progress`, { credentials: "include" }),
        fetch(`${API}/tools/swot`, { credentials: "include" }),
        fetch(`${API}/tools/values`, { credentials: "include" }),
        fetch(`${API}/insights/tool/swot`, { credentials: "include" }),
        fetch(`${API}/insights/tool/core_values`, { credentials: "include" }),
        fetch(`${API}/tools/wheel`, { credentials: "include" }),
        fetch(`${API}/gratitude/wall`, { credentials: "include" }),
      ]);
      if (progressRes.ok) setProgress(await progressRes.json());
      if (swotRes.ok) setSwot(await swotRes.json());
      if (valuesRes.ok) setValues(await valuesRes.json());
      const swotIns = swotInsightsRes.ok ? await swotInsightsRes.json() : [];
      const valIns = valuesInsightsRes.ok ? await valuesInsightsRes.json() : [];
      setInsights({ swot: swotIns, values: valIns });
      if (wheelRes.ok) {
        const wheelData = await wheelRes.json();
        setWheel(wheelData.wheel);
      }
      if (gratitudeRes.ok) {
        const g = await gratitudeRes.json();
        setGratitudeCount((g.cards || []).length);
        setGratitudePendingAsks((g.pending_asks || []).length);
      }
    } catch {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin"
          style={{ borderColor: "var(--color-brand-primary)", borderTopColor: "transparent" }} />
      </div>
    );
  }

  const swotProgress = progress?.myself?.tools?.swot?.progress || 0;
  const valuesProgress = progress?.myself?.tools?.core_values?.progress || 0;

  const strengths = (swot?.strengths || []).filter(s => !s.deleted);
  const weaknesses = (swot?.weaknesses || []).filter(s => !s.deleted);
  const opportunities = (swot?.opportunities || []).filter(s => !s.deleted);
  const threats = (swot?.threats || []).filter(s => !s.deleted);
  const swotItemCount = strengths.length + weaknesses.length + opportunities.length + threats.length;
  const valuesCount = values?.values?.length || 0;

  // Generate contextual nudge for SWOT
  let swotNudge = null;
  if (swotItemCount === 0) {
    swotNudge = "Start with what you're best at. Name one strength — that's enough to begin.";
  } else if (opportunities.length === 0 && strengths.length > 0) {
    swotNudge = `You listed ${strengths.length} strength${strengths.length > 1 ? "s" : ""} but no opportunities. What's possible that you haven't considered?`;
  } else if (weaknesses.length === 0 && strengths.length > 0) {
    swotNudge = "Self-awareness means seeing the full picture. What's one area you could improve?";
  } else if (swotProgress < 100) {
    const missing = [];
    if (strengths.length < 2) missing.push("strengths");
    if (weaknesses.length < 2) missing.push("weaknesses");
    if (opportunities.length < 2) missing.push("opportunities");
    if (threats.length < 2) missing.push("threats");
    swotNudge = `Almost there. Add more ${missing.join(" and ")} to complete your analysis.`;
  }

  // Nudge for Values
  let valuesNudge = null;
  if (valuesCount === 0) {
    valuesNudge = "62 values to explore. Your top 5 will surprise you.";
  } else if (valuesCount < 5) {
    valuesNudge = `You've identified ${valuesCount} value${valuesCount > 1 ? "s" : ""}. Aim for 5 to see what truly drives you.`;
  }

  // Wheel of Life data
  const isAdmin = user?.role === "admin";
  const wheelAreas = wheel?.areas || [];
  const wheelScoredCount = wheelAreas.filter(a => a.score).length;
  const wheelProgress = wheelAreas.length > 0 ? Math.round((wheelScoredCount / wheelAreas.length) * 100) : 0;
  const wheelAvg = wheelScoredCount > 0
    ? (wheelAreas.reduce((s, a) => s + (a.score || 0), 0) / wheelScoredCount).toFixed(1)
    : null;

  let wheelNudge = null;
  if (!wheel) {
    wheelNudge = "Pick the areas of life that matter most — then see your balance at a glance.";
  } else if (wheelScoredCount < wheelAreas.length) {
    wheelNudge = `${wheelAreas.length - wheelScoredCount} area${wheelAreas.length - wheelScoredCount > 1 ? "s" : ""} still unrated. Complete your wheel for the full picture.`;
  } else if (wheelScoredCount > 0) {
    const lowest = wheelAreas.reduce((min, a) => (a.score && a.score < (min.score || 99)) ? a : min, wheelAreas[0]);
    if (lowest && lowest.score <= 5) {
      wheelNudge = `${lowest.label} scored ${lowest.score}/10 — what's one step you could take this week?`;
    }
  }

  const upcomingTools = [
    ...(!isAdmin ? [] : []),
    { icon: Compass, name: "Purpose Discovery", question: "What gets you out of bed? Align your energy with your why.", color: "var(--color-threats-text)" },
    { icon: Brain, name: "Johari Window", question: "What do others see in you that you don't? Map your blind spots.", color: "var(--color-opportunities-text)" },
    { icon: Puzzle, name: "Personality Map", question: "What drives your decisions under pressure?", color: "var(--color-brand-primary)" },
    { icon: BarChart3, name: "Competency Models", question: "Map yourself against real frameworks. Where do you stand?", color: "var(--color-strengths-text)" },
    { icon: BookOpen, name: "Skills Inventory", question: "Which competencies define your professional edge?", color: "var(--color-weaknesses-text)" },
    { icon: TrendingUp, name: "Growth Edge Finder", question: "Where do your strengths meet your threats? That's your growth edge.", color: "var(--color-brand-mid)" },
  ];

  return (
    <div data-testid="myself-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* ── Page Header (minimal) ── */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: "var(--color-text-primary)" }}>Myself</h1>
            <p className="text-sm mt-0.5" style={{ color: "var(--color-text-secondary)" }}>Self-discovery & awareness</p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={() => navigate("/myself/portrait")}
              variant="outline"
              className="rounded-full h-9 px-4 text-sm font-medium"
              style={{ borderColor: "var(--color-brand-primary)", color: "var(--color-brand-primary)" }}
              data-testid="self-portrait-btn"
            >
              <Sparkles className="w-3.5 h-3.5 mr-1.5" /> Self-Portrait
            </Button>
          </div>
        </div>

        {/* ── Living Profile ── */}
        <div className="mb-8">
          <LivingProfile
            user={user}
            swot={swot}
            values={values}
            insights={insights}
            swotProgress={swotProgress}
            valuesProgress={valuesProgress}
            wheel={wheel}
            navigate={navigate}
          />
        </div>

        {/* ── Focus Areas Spotlight ── */}
        {<FocusSpotlight wheel={wheel} navigate={navigate} onWheelUpdate={setWheel} swotData={swot} />}

        {/* ── Your Tools ── */}
        <div className="mb-8">
          <h3 className="text-base font-semibold mb-4" style={{ color: "var(--color-text-primary)" }}>
            Your Tools
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <ToolCard
              title="Personal SWOT"
              description="Map your strengths, weaknesses, opportunities, and threats."
              icon={Target}
              iconBg="var(--color-strengths-bg)"
              iconColor="var(--color-strengths-text)"
              progress={swotProgress}
              progressColor="var(--color-strengths-text)"
              nudge={swotNudge}
              stats={
                <div className="flex gap-2">
                  {[
                    { key: "S", count: strengths.length, bg: "var(--color-strengths-bg)", color: "var(--color-strengths-text)" },
                    { key: "W", count: weaknesses.length, bg: "var(--color-weaknesses-bg)", color: "var(--color-weaknesses-text)" },
                    { key: "O", count: opportunities.length, bg: "var(--color-opportunities-bg)", color: "var(--color-opportunities-text)" },
                    { key: "T", count: threats.length, bg: "var(--color-threats-bg)", color: "var(--color-threats-text)" },
                  ].map(q => (
                    <div key={q.key} className="flex items-center gap-1 px-2 py-1 rounded-md text-xs font-semibold"
                      style={{ background: q.bg, color: q.color }}>
                      {q.key}<span className="font-normal opacity-75">{q.count}</span>
                    </div>
                  ))}
                </div>
              }
              footer={`${insights.swot.length} insight${insights.swot.length !== 1 ? "s" : ""} received`}
              onClick={() => navigate("/tools/swot")}
              testId="tool-card-swot"
            />

            <ToolCard
              title="Core Values"
              description="Identify the beliefs that guide your decisions and actions."
              icon={Heart}
              iconBg="var(--color-brand-ghost)"
              iconColor="var(--color-brand-primary)"
              progress={valuesProgress}
              progressColor="var(--color-brand-primary)"
              nudge={valuesNudge}
              stats={
                valuesCount > 0 ? (
                  <div className="flex gap-1.5 flex-wrap">
                    {(values?.values || []).sort((a, b) => a.priority - b.priority).slice(0, 4).map(v => (
                      <span key={v.value_id} className="text-xs font-medium px-2 py-0.5 rounded-full"
                        style={{ background: "var(--color-brand-ghost)", color: "var(--color-brand-primary)", border: "0.5px solid var(--color-border-active)" }}>
                        {v.name}
                      </span>
                    ))}
                    {valuesCount > 4 && (
                      <span className="text-xs px-2 py-0.5" style={{ color: "var(--color-text-placeholder)" }}>
                        +{valuesCount - 4}
                      </span>
                    )}
                  </div>
                ) : null
              }
              footer={`${insights.values.length} insight${insights.values.length !== 1 ? "s" : ""} received`}
              onClick={() => navigate("/tools/values")}
              testId="tool-card-values"
            />

            {/* Wheel of Life */}
            {(
              <ToolCard
                title="Wheel of Life"
                description="Rate key life areas and visualize your balance."
                icon={CircleDot}
                iconBg="#14b8a615"
                iconColor="#14b8a6"
                progress={wheelProgress}
                progressColor="#14b8a6"
                nudge={wheelNudge}
                stats={
                  wheelAreas.length > 0 ? (
                    <div className="flex gap-1.5 flex-wrap">
                      {wheelAreas.slice(0, 4).map(a => (
                        <span key={a.key} className="text-xs font-medium px-2 py-0.5 rounded-full"
                          style={{ background: `${a.color}15`, color: a.color, border: `0.5px solid ${a.color}30` }}>
                          {a.label} {a.score ? `${a.score}` : ""}
                        </span>
                      ))}
                      {wheelAreas.length > 4 && (
                        <span className="text-xs px-2 py-0.5" style={{ color: "var(--color-text-placeholder)" }}>
                          +{wheelAreas.length - 4}
                        </span>
                      )}
                    </div>
                  ) : null
                }
                footer={wheelAvg ? `Average: ${wheelAvg}/10 across ${wheelAreas.length} areas` : "Not started yet"}
                onClick={() => navigate("/tools/wheel")}
                testId="tool-card-wheel"
              />
            )}

            {/* Gratitude Wall */}
            <ToolCard
              title="Gratitude Wall"
              description="Pin notes to yourself, offer gratitude to your Circle, ask how you matter to them."
              icon={HeartHandshake}
              iconBg="var(--color-brand-ghost)"
              iconColor="var(--color-brand-primary)"
              progress={Math.min(100, gratitudeCount * 10)}
              progressColor="var(--color-brand-primary)"
              nudge={
                gratitudePendingAsks > 0
                  ? `${gratitudePendingAsks} ${gratitudePendingAsks === 1 ? "person is" : "people are"} waiting to hear what you're grateful for in them.`
                  : gratitudeCount === 0
                    ? "Start with breakfast. Or the sky. The first note is always the hardest."
                    : gratitudeCount < 5
                      ? "A wall is built one small note at a time. Keep going."
                      : null
              }
              stats={
                gratitudeCount > 0 ? (
                  <div className="flex gap-1.5 flex-wrap">
                    <span className="text-xs font-medium px-2 py-0.5 rounded-full"
                      style={{ background: "var(--color-brand-ghost)", color: "var(--color-brand-primary)", border: "0.5px solid var(--color-border-active)" }}>
                      {gratitudeCount} card{gratitudeCount !== 1 ? "s" : ""}
                    </span>
                    {gratitudePendingAsks > 0 && (
                      <span className="text-xs font-medium px-2 py-0.5 rounded-full"
                        style={{ background: "var(--color-opportunities-bg)", color: "var(--color-opportunities-text)", border: "0.5px solid var(--color-opportunities-border)" }}>
                        {gratitudePendingAsks} ask{gratitudePendingAsks !== 1 ? "s" : ""} pending
                      </span>
                    )}
                  </div>
                ) : null
              }
              footer={gratitudeCount === 0 ? "Not started yet" : `${gratitudeCount} note${gratitudeCount !== 1 ? "s" : ""} on your wall`}
              onClick={() => navigate("/tools/gratitude")}
              testId="tool-card-gratitude"
            />
          </div>
        </div>

        {/* ── Explore Next (upcoming tools as teasers) ── */}
        <div>
          <h3 className="text-base font-semibold mb-1" style={{ color: "var(--color-text-primary)" }}>
            Explore Next
          </h3>
          <p className="text-xs mb-4" style={{ color: "var(--color-text-secondary)" }}>
            More tools on the way. Each one adds a new dimension to your self-portrait.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {upcomingTools.map(tool => (
              <UpcomingTeaser key={tool.name} {...tool} />
            ))}
          </div>
        </div>

      </div>
    </div>
  );
};

export default MyselfPage;
